(() => {
  if (window.__cartRewardsInit) return;
  window.__cartRewardsInit = true;

  const routes = window.routes || {};
  const ADD_URL = (routes.cart_add_url || '/cart/add') + '.js';
  const CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  const CART_URL = (routes.cart_url || '/cart') + '.js';

  let syncing = false;
  let syncQueued = false;
  let modalTierAmount = null;
  let modalMode = null; // 'choice' | 'progression'
  let modalSelection = null;
  let modalExistingLineKey = null;
  let modalOriginalVariantId = null;
  let lastFocusedElement = null;
  let lastKnownFillRatio = null;

  // Tracks the subtotal as of the last sync so we can tell a genuinely NEW
  // threshold crossing (subtotal moves from below a tier to at/above it)
  // apart from "still above a tier I was already above" — the latter must
  // never re-trigger the popup (e.g. on page load, or on every 2s poll).
  //
  // Persisted to sessionStorage (not just an in-memory variable): this script
  // re-initializes from scratch on every full page navigation, so a plain
  // variable would reset to null every time a shopper moves from one product
  // page to another. That silently ate real crossings — e.g. add a 35€ item
  // on page A, navigate to page B, add a 65€ item there: page B's baseline
  // would start fresh at 35€ instead of carrying the true session start, and
  // whichever tier fell between "true start" and 35€ was never seen as
  // crossed. Reading/writing sessionStorage keeps the baseline correct across
  // navigations while still resetting for a genuinely new browser session.
  const BASELINE_STORAGE_KEY = 'mf_cart_reward_baseline';
  let subtotalBaseline = null;
  try {
    const storedBaseline = sessionStorage.getItem(BASELINE_STORAGE_KEY);
    if (storedBaseline !== null) subtotalBaseline = parseFloat(storedBaseline);
  } catch (e) {}
  function setSubtotalBaseline(value) {
    subtotalBaseline = value;
    try { sessionStorage.setItem(BASELINE_STORAGE_KEY, String(value)); } catch (e) {}
  }
  // Tier amounts (numbers) crossed in the current batch of cart changes but
  // not yet shown a choice screen — drained one at a time so a single add
  // that jumps several tiers at once chains through them in order.
  let crossingQueue = [];
  // key -> quantity snapshot of real (non-reward) cart lines as of the last
  // sync, used to detect genuine adds/quantity-increases for Klaviyo/Shopify
  // analytics reporting — see detectNewlyAddedItems() for why this exists
  // instead of the fetch-wrap in reportAddedToCart's own instrumentation.
  let cartItemSnapshot = null;

  const TIER_ICONS = ['🌱', '🌸', '🌿', '💎'];
  const TIER_CLASSES = ['graine', 'fleur', 'plantation', 'trafiquant'];

  // Rarity word shown for the tier being approached (index into
  // tierBlocks()) — shared between the progression screen's title and the
  // in-drawer bar text below, so the hype language stays consistent
  // wherever a tier is teased. No entry for index 0 (Graine) — that's the
  // generic first-threshold nudge, not an "interpalier".
  const TIER_RARITY_WORDS = [null, 'RARES', 'ÉPIQUES', 'LÉGENDAIRES'];

  // Hype copy for the progression screen's title.
  const PROGRESSION_TITLES = [
    null,
    (amt) => `Encore ${amt} € et tu débloques tes échantillons RARES…`,
    (amt) => `Encore ${amt} € et tu débloques tes échantillons ÉPIQUES…`,
    (amt) => `Encore ${amt} € et tu débloques tes échantillons LÉGENDAIRES…`,
  ];

  const moneyToNumber = (cents) => cents / 100;

  /* ---------- Analytics: report real add-to-cart events to Shopify + Klaviyo ----------
     The theme's cart engine (loaded from js.shrinetheme.com) posts straight to
     Shopify's Ajax Cart API without ever calling Shopify.analytics.publish(), so
     neither Shopify's own pixel system nor Klaviyo's "Added to Cart" tracking ever
     fires — confirmed empirically (zero publish calls, empty _learnq) for both the
     stock product-form and our own quick-add buttons. Wrapping fetch() here is the
     only hook point available since that remote bundle isn't ours to edit. Reward-
     tier auto-adds (identified by the _reward_tier line property) are skipped since
     those aren't a genuine shopper action. */
  let lastReportedAdd = null;

  function reportAddedToCart(item) {
    if (!item || !item.id) return;
    if (item.properties && item.properties._reward_tier) return;

    const dedupeKey = `${item.id}:${item.quantity}`;
    const now = Date.now();
    if (lastReportedAdd && lastReportedAdd.key === dedupeKey && now - lastReportedAdd.t < 2000) return;
    lastReportedAdd = { key: dedupeKey, t: now };

    const price = moneyToNumber(item.final_price != null ? item.final_price : item.price);
    const payload = {
      product_id: item.product_id,
      variant_id: item.variant_id || item.id,
      sku: item.sku,
      title: item.product_title || item.title,
      variant_title: item.variant_title,
      quantity: item.quantity,
      price,
      url: item.url,
      image: item.image,
    };

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      try {
        window.Shopify.analytics.publish('product_added_to_cart', payload);
      } catch (e) {}
    }

    if (window._learnq) {
      window._learnq.push([
        'track',
        'Added to Cart',
        {
          $value: price * item.quantity,
          AddedItemProductName: payload.title,
          AddedItemProductID: payload.product_id,
          AddedItemVariantID: payload.variant_id,
          AddedItemSKU: payload.sku,
          AddedItemQuantity: payload.quantity,
          AddedItemPrice: price,
          AddedItemURL: payload.url ? location.origin + payload.url : undefined,
          AddedItemImageURL: payload.image,
        },
      ]);
    }
  }

  (function instrumentAddToCart() {
    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (typeof input !== 'string' && input && input.method) || 'GET').toUpperCase();
      const promise = originalFetch.apply(this, arguments);

      // Different add-to-cart paths on this site hit different endpoint variants:
      // our own reward code calls /cart/add.js, but the remote cart engine's
      // intercepted product-form submit calls bare /cart/add. Match both; the
      // dedupe guard in reportAddedToCart collapses any double-fire from a
      // single click hitting both endpoints. Change/update/clear are also
      // watched (add-only) so the announcement bar and tier automation stay in
      // sync with quantity edits and removals made anywhere on the site, not
      // just from inside our own drawer code.
      if (method !== 'POST') return promise;
      const isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      const isCartMutation = isAdd || /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url);
      if (!isCartMutation) return promise;

      promise
        .then((res) => (res.ok ? res.clone().json() : null))
        .then((data) => {
          if (isAdd && data) {
            const items = Array.isArray(data.items) ? data.items : [data];
            items.forEach(reportAddedToCart);
          }
          if (data) syncTiers();
        })
        .catch(() => {});

      return promise;
    };
  })();

  async function getCart() {
    const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function addVariant(variantId, properties) {
    const res = await fetch(ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties || {} }] }),
    });
    return res.json();
  }

  async function changeLine(lineKey, quantity) {
    // Shopify's /cart/change.js "line" param is a 1-based position index, not the
    // line-item key — the key must be passed as "id" instead.
    const res = await fetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    return res.json();
  }

  const getSectionInnerHTML = (html, selector) =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

  async function refreshDrawer() {
    // Use the homepage as a stable path for the Section Rendering API request:
    // product pages and /cart both special-case the "sections" query param on
    // this store and return unrelated JSON instead of the rendered section.
    const url = `/?sections=cart-drawer&_=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    const data = await res.json();
    const html = data['cart-drawer'];
    if (!html) return;

    const freshBody = getSectionInnerHTML(html, '.cart-drawer__body');
    const liveBody = document.querySelector('.cart-drawer__body');
    if (freshBody && liveBody) liveBody.innerHTML = freshBody.innerHTML;

    const freshFooter = getSectionInnerHTML(html, '.drawer__footer');
    const liveFooter = document.querySelector('.drawer__footer');
    if (freshFooter && liveFooter) liveFooter.innerHTML = freshFooter.innerHTML;

    const freshHead = getSectionInnerHTML(html, '.mf-head__meta');
    const liveHead = document.querySelector('.mf-head__meta');
    if (freshHead && liveHead) liveHead.innerHTML = freshHead.innerHTML;

    const cartDrawerRoot = document.querySelector('cart-drawer');
    if (cartDrawerRoot) {
      // Scoped to the cart-items wrapper specifically: a bare ".is-empty"
      // query also matches unrelated "no gift chosen yet" markers (e.g.
      // .cart-gifts.is-empty), which are common even when the cart has real
      // items and would wrongly blank out the whole drawer.
      const isEmpty = !!getSectionInnerHTML(html, 'cart-drawer-items.is-empty');
      cartDrawerRoot.classList.toggle('is-empty', isEmpty);
    }

    updateCartBubble();
    observer.disconnect();
    observeCartItems();
  }

  function updateCartBubble() {
    getCart().then((cart) => {
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span');
      if (bubble) bubble.textContent = cart.item_count;

      const floatingCount = document.querySelector('[data-floating-cart-count]');
      if (floatingCount) {
        floatingCount.textContent = cart.item_count;
        floatingCount.hidden = cart.item_count <= 0;
      }
    });
  }

  const tierBlocks = () => Array.from(document.querySelectorAll('[data-reward-tier]'));

  function formatMoney(amountCents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.theme_money_format) {
      return Shopify.formatMoney(amountCents, window.theme_money_format);
    }
    return (amountCents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function computeRewardStep(tierEls, subtotal) {
    const amounts = tierEls.map((el) => parseFloat(el.dataset.tierAmount));
    let stepIndex = amounts.findIndex((amount) => subtotal < amount);
    const allDone = stepIndex === -1;
    if (allDone) stepIndex = amounts.length - 1;
    const prevAmount = stepIndex > 0 ? amounts[stepIndex - 1] : 0;
    const targetAmount = amounts[stepIndex];
    const span = Math.max(0.01, targetAmount - prevAmount);
    const ratio = allDone ? 100 : Math.min(100, Math.max(0, ((subtotal - prevAmount) * 100) / span));
    return { amounts, stepIndex, allDone, prevAmount, targetAmount, ratio, targetEl: tierEls[stepIndex] };
  }

  function renderBar(subtotal) {
    const tierEls = tierBlocks();
    if (!tierEls.length) return;
    const { amounts, stepIndex, allDone, prevAmount, targetAmount, ratio } = computeRewardStep(tierEls, subtotal);

    const fill = document.querySelector('[data-rewards-fill]');
    if (fill) {
      // A drawer refresh (ours or the remote cart engine's) replaces this
      // element wholesale with the target width already baked into its
      // inline style, so the CSS transition has no "before" state to
      // animate from. Reintroduce one by snapping to the last known ratio
      // (remembered across refreshes) before animating to the new value.
      if (lastKnownFillRatio !== null) {
        fill.style.transition = 'none';
        fill.style.width = `${lastKnownFillRatio}%`;
        void fill.offsetWidth;
        fill.style.transition = '';
      }
      fill.style.width = `${ratio}%`;
    }
    lastKnownFillRatio = ratio;

    const targetEl = tierEls[stepIndex];
    const stepIcon = document.querySelector('[data-rewards-step-icon]');
    const stepText = document.querySelector('[data-rewards-step-text]');
    const stepBadge = document.querySelector('[data-rewards-step-badge]');
    const boundPrev = document.querySelector('[data-rewards-bound-prev]');
    const boundTarget = document.querySelector('[data-rewards-bound-target]');

    if (stepIcon) {
      stepIcon.textContent = TIER_ICONS[stepIndex] || '✨';
      const iconColors = TIER_BAR_COLORS[stepIndex];
      if (iconColors) {
        stepIcon.style.setProperty('--rewards-icon-color', iconColors.bg);
      } else {
        stepIcon.style.removeProperty('--rewards-icon-color');
      }
    }
    if (allDone) {
      if (stepText) stepText.innerHTML = '<span class="rewards-bar__done">🎉 Tous les cadeaux débloqués !</span>';
      if (stepBadge) stepBadge.textContent = '';
    } else {
      const remainingCents = Math.round((targetAmount - subtotal) * 100);
      if (stepText) {
        const rarityWord = TIER_RARITY_WORDS[stepIndex];
        const tail = rarityWord
          ? `et choisir tes échantillons ${rarityWord}…`
          : `et choisir ton échantillon offert…`;
        stepText.innerHTML = `Plus que <strong>${formatMoney(remainingCents)}</strong> pour débloquer le palier <span class="rewards-bar__target">${targetEl.dataset.tierLabel}</span> ${tail}`;
      }
      if (stepBadge) stepBadge.textContent = `Étape ${stepIndex + 1}/${tierEls.length}`;
    }
    if (boundPrev) boundPrev.textContent = `${Math.round(prevAmount)}€`;
    if (boundTarget) boundTarget.textContent = `${Math.round(targetAmount)}€ · ${targetEl.dataset.tierLabel}`;

    document.querySelectorAll('[data-rewards-dots] .rewards-bar__dot').forEach((dot, i) => {
      dot.classList.toggle('is-reached', subtotal >= amounts[i]);
      dot.classList.toggle('is-current', !allDone && i === stepIndex);
    });
  }

  // Fixed palette for the 4 tiers (light green / orange / deep green / blue),
  // each paired with a text color that stays readable on it — used by both
  // the gift modal (via CSS classes) and this top bar (set inline, since the
  // bar's base colors are merchant-configurable theme-editor settings).
  const TIER_BAR_COLORS = [
    { bg: '#4ade80', text: '#052e16' },
    { bg: '#fb923c', text: '#431407' },
    { bg: '#a855f7', text: '#ffffff' },
    { bg: '#3b82f6', text: '#ffffff' },
  ];

  // Top announcement bar: shows the static promo message while the cart is
  // empty, and swaps to a live "next reward" progress bar — colored to match
  // the active tier — as soon as it isn't. Reuses the same tier data the
  // drawer's rewards bar already reads, so there's a single source of truth
  // for tier order/labels.
  // Keeps the floating cart button (shown once the header scrolls out of
  // view) in the same color as the threshold bar, instead of a fixed green —
  // called from the same place that colors the bar itself so they can never
  // drift out of sync.
  function applyFloatingCartColor(colors) {
    const floatingBtn = document.querySelector('[data-floating-cart-btn]');
    if (floatingBtn) {
      if (colors) {
        floatingBtn.style.setProperty('--floating-cart-bg', colors.bg);
        floatingBtn.style.setProperty('--floating-cart-text', colors.text);
      } else {
        floatingBtn.style.removeProperty('--floating-cart-bg');
        floatingBtn.style.removeProperty('--floating-cart-text');
      }
    }

    const cartHeadIcon = document.querySelector('[data-cart-head-icon]');
    if (cartHeadIcon) {
      if (colors) {
        cartHeadIcon.style.setProperty('--cart-head-color', colors.bg);
      } else {
        cartHeadIcon.style.removeProperty('--cart-head-color');
      }
    }
  }

  function renderAnnounceBar(subtotal) {
    const wrapperEl = document.querySelector('[data-announce-wrapper]');
    const staticEl = document.querySelector('[data-announce-static]');
    const rewardsEl = document.querySelector('[data-announce-rewards]');
    if (!rewardsEl) return;

    const tierEls = tierBlocks();
    if (!tierEls.length || subtotal <= 0) {
      if (staticEl) staticEl.hidden = false;
      rewardsEl.hidden = true;
      if (wrapperEl) {
        wrapperEl.style.removeProperty('--promo-bg-color');
        wrapperEl.style.removeProperty('--promo-text-color');
      }
      applyFloatingCartColor(null);
      return;
    }

    const { allDone, targetAmount, targetEl, ratio } = computeRewardStep(tierEls, subtotal);
    const stepIndex = tierEls.indexOf(targetEl);
    const colors = TIER_BAR_COLORS[stepIndex] || TIER_BAR_COLORS[TIER_BAR_COLORS.length - 1];

    if (staticEl) staticEl.hidden = true;
    rewardsEl.hidden = false;
    if (wrapperEl) {
      wrapperEl.style.setProperty('--promo-bg-color', colors.bg);
      wrapperEl.style.setProperty('--promo-text-color', colors.text);
    }
    applyFloatingCartColor(colors);

    const iconEl = document.querySelector('[data-announce-icon]');
    const labelEl = document.querySelector('[data-announce-label]');
    const progressEl = document.querySelector('[data-announce-progress]');
    const fillEl = document.querySelector('[data-announce-fill]');
    const percentEl = document.querySelector('[data-announce-percent]');
    const dividerEl = document.querySelector('[data-announce-divider]');
    const amountWrapEl = document.querySelector('[data-announce-amount-wrap]');
    const amountEl = document.querySelector('[data-announce-amount]');

    if (iconEl) iconEl.textContent = TIER_ICONS[stepIndex] || '✨';

    if (allDone) {
      if (labelEl) labelEl.textContent = '🎉 Tous les cadeaux débloqués !';
      if (progressEl) progressEl.hidden = true;
      if (dividerEl) dividerEl.hidden = true;
      if (amountWrapEl) amountWrapEl.hidden = true;
    } else {
      const remainingCents = Math.round((targetAmount - subtotal) * 100);
      if (labelEl) labelEl.textContent = `Plus que ${formatMoney(remainingCents)} pour Palier ${targetEl.dataset.tierLabel}`;
      if (progressEl) progressEl.hidden = false;
      if (dividerEl) dividerEl.hidden = false;
      if (amountWrapEl) amountWrapEl.hidden = false;
      if (fillEl) fillEl.style.width = `${ratio}%`;
      if (percentEl) percentEl.textContent = `${Math.round(ratio)}%`;
      if (amountEl) amountEl.textContent = formatMoney(remainingCents);
    }
  }

  function detectNewCrossings(tierEls, prevSubtotal, nextSubtotal) {
    const crossed = [];
    for (const el of tierEls) {
      const amount = parseFloat(el.dataset.tierAmount);
      if (prevSubtotal < amount && nextSubtotal >= amount) crossed.push(amount);
    }
    return crossed.sort((a, b) => a - b);
  }

  // The theme's own add-to-cart flow (remote bundle) posts straight to the
  // Ajax Cart API without ever touching Shopify.analytics.publish, and it
  // can replace window.fetch after this script runs (observed in production:
  // window.fetch stops being our wrapper), silently breaking the fetch-wrap
  // instrumentation in reportAddedToCart below for real add-to-cart clicks.
  // getCart()'s plain GET is unaffected by that, so diffing real (non-
  // reward) line quantities across syncTiers() calls — which already fires
  // reliably via the drawer-open observer + 2s poll — is the robust signal.
  function detectNewlyAddedItems(items) {
    const nextSnapshot = new Map();
    const added = [];
    for (const item of items) {
      if (item.properties && item.properties._reward_tier) continue;
      nextSnapshot.set(item.key, item.quantity);
      if (cartItemSnapshot === null) continue; // first sync: establish baseline, don't report
      const prevQty = cartItemSnapshot.get(item.key) || 0;
      if (item.quantity > prevQty) added.push({ ...item, quantity: item.quantity - prevQty });
    }
    cartItemSnapshot = nextSnapshot;
    return added;
  }

  async function syncTiers() {
    if (syncing) {
      syncQueued = true;
      return;
    }
    syncing = true;
    try {
      const cart = await getCart();
      const subtotal = moneyToNumber(cart.items_subtotal_price);
      let mutated = false;

      // An empty cart (checkout completed, or everything removed) is a clean
      // slate — realign the persisted baseline to 0 so the next order in
      // this same browser tab correctly detects crossings from the start,
      // instead of comparing against a stale baseline left over from before.
      if (subtotal === 0 && subtotalBaseline !== 0) {
        setSubtotalBaseline(0);
      }

      detectNewlyAddedItems(cart.items).forEach(reportAddedToCart);

      renderBar(subtotal);
      renderAnnounceBar(subtotal);

      const tierEls = tierBlocks();

      // First sync of the page just establishes the baseline — tiers already
      // unlocked from a previous visit must never pop the modal on load.
      if (subtotalBaseline === null) {
        setSubtotalBaseline(subtotal);
      } else if (subtotal !== subtotalBaseline) {
        const newlyCrossed = detectNewCrossings(tierEls, subtotalBaseline, subtotal);
        setSubtotalBaseline(subtotal);
        for (const amount of newlyCrossed) {
          if (!crossingQueue.includes(amount)) crossingQueue.push(amount);
        }
      }

      // Drop the reward line for any tier the subtotal has fallen back
      // below (e.g. an item was removed) — the customer must pick again if
      // they cross back over it later, which detectNewCrossings() already
      // handles on its own. Also drop a line whose product isn't in this
      // tier's current gift list at all — a leftover from a reward config
      // that has since changed (a swapped sample, a retired free-shipping-
      // style tier reusing the same threshold, etc.) has no business being
      // displayed as "your chosen gift" for this tier.
      for (const tierEl of tierEls) {
        const minAmount = parseFloat(tierEl.dataset.tierAmount);
        const unlocked = subtotal >= minAmount;
        const existingLine = cart.items.find(
          (item) => item.properties && item.properties._reward_tier === tierEl.dataset.tierAmount
        );
        let orphaned = false;
        if (existingLine && tierEl.dataset.tierType === 'gift_choice') {
          const validIds = (tierEl.dataset.tierGiftVariantIds || '').split(',').filter(Boolean);
          orphaned = validIds.length > 0 && !validIds.includes(String(existingLine.variant_id));
        }
        if ((!unlocked || orphaned) && existingLine) {
          await changeLine(existingLine.key, 0);
          mutated = true;
          crossingQueue = crossingQueue.filter((a) => a !== minAmount);
          if (modalTierAmount === tierEl.dataset.tierAmount) closeGiftModal();
        }
      }

      if (mutated) await refreshDrawer();

      // A progression screen tracks "next tier" purely for display — if a
      // further cart change (from anywhere: this tab, quick-add elsewhere)
      // crosses that very tier while it's still showing, its "remaining"
      // text would otherwise go stale and the newly-unlocked choice would
      // never surface until the modal happened to close and reopen. Any
      // open progression screen is safe to interrupt with the new choice;
      // a choice screen in progress is left alone so we don't yank a
      // decision out from under the customer mid-click.
      if ((modalTierAmount === null || modalMode === 'progression') && crossingQueue.length) {
        const nextAmount = crossingQueue.shift();
        await openChoiceScreen(String(nextAmount));
      }
    } finally {
      syncing = false;
      if (syncQueued) {
        syncQueued = false;
        syncTiers();
      }
    }
  }

  /* ---------- Gift modal (select then confirm, or view progress) ---------- */

  function setConfirmState() {
    const confirm = document.querySelector('[data-gift-confirm]');
    if (!confirm) return;
    confirm.disabled = !modalSelection;
  }

  // Marks each stepper pill as done (before the active one), active (the
  // tier currently shown — as a choice or as a progression target), or
  // untouched (still ahead, default grey styling).
  function renderStepper(activeIndex) {
    document.querySelectorAll('[data-gift-step]').forEach((stepEl, i) => {
      stepEl.classList.toggle('is-done', i < activeIndex);
      stepEl.classList.toggle('is-active', i === activeIndex);
    });
  }

  async function openChoiceScreen(tierAmount) {
    const modal = document.querySelector('[data-gift-modal]');
    if (!modal) return;
    const tierEls = tierBlocks();
    const tierIndex = tierEls.findIndex((el) => el.dataset.tierAmount === tierAmount);
    if (tierIndex === -1) return;
    const tierEl = tierEls[tierIndex];

    modalTierAmount = tierAmount;
    modalMode = 'choice';

    // Determine current chosen sample for this tier — remembered so confirmGift()
    // doesn't need to re-fetch the cart just to re-derive the same answer.
    const cart = await getCart();
    const existingLine = cart.items.find(
      (item) => item.properties && item.properties._reward_tier === tierAmount
    );
    modalSelection = existingLine ? String(existingLine.variant_id) : null;
    modalExistingLineKey = existingLine ? existingLine.key : null;
    modalOriginalVariantId = modalSelection;

    const dialog = modal.querySelector('[data-gift-dialog]');
    if (dialog) dialog.className = 'gift-modal__dialog tier-' + TIER_CLASSES[tierIndex];

    const eyebrow = modal.querySelector('[data-gift-eyebrow]');
    const title = modal.querySelector('[data-gift-title]');
    const subtitle = modal.querySelector('[data-gift-subtitle]');
    const headerIcon = modal.querySelector('[data-gift-header-icon]');
    if (eyebrow) eyebrow.textContent = `✨ Cadeau débloqué · Palier ${tierIndex + 1}/${tierEls.length}`;
    if (title) title.textContent = `Palier ${tierEl.dataset.tierLabel} atteint ${TIER_ICONS[tierIndex]}`;
    if (subtitle) subtitle.textContent = 'Choisis ton échantillon offert — un par palier.';
    if (headerIcon) headerIcon.textContent = '🎁';

    modal.querySelectorAll('[data-gift-panel]').forEach((panel) => {
      const isThis = panel.dataset.tierAmount === tierAmount;
      panel.hidden = !isThis;
      panel.classList.remove('is-locked');
    });
    modal.querySelectorAll('[data-gift-option]').forEach((opt) => {
      opt.classList.toggle('is-selected', modalSelection !== null && opt.dataset.variantId === modalSelection);
    });

    const label = modal.querySelector('[data-gift-confirm-label]');
    if (label) label.textContent = existingLine ? 'Confirmer' : 'Ajouter mon cadeau';
    setConfirmState();

    const progressEl = modal.querySelector('[data-gift-progress]');
    if (progressEl) progressEl.hidden = true;
    const footerChoice = modal.querySelector('[data-gift-footer-choice]');
    const footerProgress = modal.querySelector('[data-gift-footer-progress]');
    if (footerChoice) footerChoice.hidden = false;
    if (footerProgress) footerProgress.hidden = true;

    renderStepper(tierIndex);
    const stepperEl = modal.querySelector('[data-gift-stepper]');
    if (stepperEl) stepperEl.hidden = false;
    const unlockBarEl = modal.querySelector('[data-gift-unlock-bar]');
    if (unlockBarEl) unlockBarEl.hidden = true;

    lastFocusedElement = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('overflow-hidden');
  }

  async function openProgressionScreen() {
    const modal = document.querySelector('[data-gift-modal]');
    if (!modal) return;
    const tierEls = tierBlocks();
    const cart = await getCart();
    const subtotal = moneyToNumber(cart.items_subtotal_price);
    const { allDone, targetEl, prevAmount, targetAmount, ratio } = computeRewardStep(tierEls, subtotal);

    // Every tier is already unlocked — nothing left to progress toward.
    if (allDone) {
      closeGiftModal();
      return;
    }

    const targetIndex = tierEls.indexOf(targetEl);
    modalTierAmount = null;
    modalMode = 'progression';

    // The progression screen is always branded in the site's neutral neon
    // green, not the target tier's color — it's a "you're on your way"
    // state, distinct from the tier-colored "you unlocked it" choice screen.
    const dialog = modal.querySelector('[data-gift-dialog]');
    if (dialog) dialog.className = 'gift-modal__dialog';

    const remainingWhole = Math.round(targetAmount - subtotal);
    const subtotalWhole = Math.round(subtotal);
    const targetWhole = Math.round(targetAmount);
    const tierLabel = targetEl.dataset.tierLabel;
    const eyebrow = modal.querySelector('[data-gift-eyebrow]');
    const title = modal.querySelector('[data-gift-title]');
    const subtitle = modal.querySelector('[data-gift-subtitle]');
    const headerIcon = modal.querySelector('[data-gift-header-icon]');
    if (eyebrow) eyebrow.textContent = `✨ En route vers ${tierLabel}`;
    const titleFn = PROGRESSION_TITLES[targetIndex];
    if (title) title.textContent = titleFn ? titleFn(remainingWhole) : `Encore ${remainingWhole} € et c'est à toi`;
    if (subtitle) subtitle.textContent = `Ajoute ${remainingWhole} € pour débloquer l'un de ces 4 échantillons ${tierLabel.toLowerCase()} offerts.`;
    if (headerIcon) headerIcon.textContent = '🎁';

    const progressEl = modal.querySelector('[data-gift-progress]');
    if (progressEl) progressEl.hidden = false;
    const remainingEl = modal.querySelector('[data-gift-progress-remaining]');
    const cartEl = modal.querySelector('[data-gift-progress-cart]');
    if (remainingEl) remainingEl.textContent = `${remainingWhole}€`;
    if (cartEl) cartEl.innerHTML = `Panier <strong>${subtotalWhole}€</strong> / ${targetWhole}€`;

    modal.querySelectorAll('[data-gift-panel]').forEach((panel) => {
      const isTarget = panel.dataset.tierAmount === targetEl.dataset.tierAmount;
      panel.hidden = !isTarget;
      panel.classList.toggle('is-locked', isTarget);
    });

    const footerChoice = modal.querySelector('[data-gift-footer-choice]');
    const footerProgress = modal.querySelector('[data-gift-footer-progress]');
    if (footerChoice) footerChoice.hidden = true;
    if (footerProgress) footerProgress.hidden = false;

    const stepperEl = modal.querySelector('[data-gift-stepper]');
    if (stepperEl) stepperEl.hidden = true;

    // Mobile-only checkpoint bar: a coarser, always-visible companion to the
    // stat row above — evenly-spaced tier checkpoints with a fill line
    // proportional to overall subtotal (not just progress within this
    // segment), matching the reference design exactly.
    const unlockBarEl = modal.querySelector('[data-gift-unlock-bar]');
    if (unlockBarEl) {
      unlockBarEl.hidden = false;
      const maxAmount = parseFloat(tierEls[tierEls.length - 1].dataset.tierAmount);
      const unlockRatio = Math.max(0, Math.min(100, (subtotal / maxAmount) * 100));
      const targetColor = (TIER_BAR_COLORS[targetIndex] || TIER_BAR_COLORS[TIER_BAR_COLORS.length - 1]).bg;
      unlockBarEl.style.setProperty('--unlock-target-color', targetColor);
      const fillEl = unlockBarEl.querySelector('[data-gift-unlock-bar-fill]');
      if (fillEl) fillEl.style.width = `${unlockRatio}%`;
      const amountEl = unlockBarEl.querySelector('[data-gift-unlock-bar-amount]');
      if (amountEl) amountEl.innerHTML = `Panier <strong>${subtotalWhole}€</strong> / ${Math.round(maxAmount)}€`;
      unlockBarEl.querySelectorAll('[data-gift-unlock-point]').forEach((point) => {
        const amount = parseFloat(point.dataset.tierAmount);
        point.classList.toggle('is-done', subtotal >= amount);
        point.classList.toggle('is-target', point.dataset.tierAmount === targetEl.dataset.tierAmount);
      });
    }

    lastFocusedElement = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('overflow-hidden');
  }

  function closeGiftModal() {
    const modal = document.querySelector('[data-gift-modal]');
    if (!modal) return;
    modal.hidden = true;
    modalTierAmount = null;
    modalMode = null;
    modalSelection = null;
    modalExistingLineKey = null;
    modalOriginalVariantId = null;
    crossingQueue = [];
    document.body.classList.remove('overflow-hidden');
    if (lastFocusedElement && document.contains(lastFocusedElement)) lastFocusedElement.focus();
  }

  async function confirmGift() {
    // Defensive re-derivation from the DOM: if the drawer's markup gets
    // replaced wholesale by the storefront's own remote cart bundle (it does
    // this outside our control — see notes elsewhere in this file) between a
    // gift pick and hitting Confirm, these module-level variables can end up
    // stale/null while the modal still visually shows a selection, making
    // the button silently do nothing. Falling back to what's actually on
    // screen keeps the click working even after such a refresh.
    if (!modalSelection) {
      const selectedOption = document.querySelector('[data-gift-option].is-selected');
      if (selectedOption) modalSelection = selectedOption.dataset.variantId;
    }
    if (!modalTierAmount) {
      const activePanel = document.querySelector('[data-gift-panel]:not([hidden])');
      if (activePanel) modalTierAmount = activePanel.dataset.tierAmount;
    }
    if (!modalTierAmount || !modalSelection) return;
    const tierAmount = modalTierAmount;
    const variantId = modalSelection;
    const existingLineKey = modalExistingLineKey;

    const confirmBtn = document.querySelector('[data-gift-confirm]');
    const confirmLabel = document.querySelector('[data-gift-confirm-label]');
    const originalLabel = confirmLabel ? confirmLabel.textContent : '';

    // Re-confirming the sample they already had — nothing to change on the
    // cart, but still advance the flow (next queued tier, or progression).
    if (existingLineKey && variantId === modalOriginalVariantId) {
      await advanceAfterChoice();
      return;
    }

    if (confirmBtn) confirmBtn.disabled = true;
    if (confirmLabel) confirmLabel.textContent = 'Ajout en cours…';

    try {
      // existingLineKey is only set when the customer already had a sample
      // for this tier; nothing to remove when it's their first pick.
      if (existingLineKey) {
        await changeLine(existingLineKey, 0);
      }
      await addVariant(variantId, { _reward_tier: tierAmount });
      // Not awaited: the flow already moves on below, so refreshing the
      // drawer's HTML is just cosmetic sync from here — no reason to block.
      refreshDrawer();
      await advanceAfterChoice();
    } catch (e) {
      if (confirmBtn) confirmBtn.disabled = false;
      if (confirmLabel) confirmLabel.textContent = originalLabel;
    }
  }

  // After a gift is confirmed: if crossing multiple tiers unlocked more than
  // one choice screen in this batch, show the next one; otherwise switch the
  // same modal into the progression view toward whichever tier is next.
  async function advanceAfterChoice() {
    if (crossingQueue.length) {
      const nextAmount = crossingQueue.shift();
      await openChoiceScreen(String(nextAmount));
    } else {
      await openProgressionScreen();
    }
  }

  // Opens the side cart the same way the theme's own quick-add flow does
  // (toggling the drawer/overlay classes directly) rather than navigating to
  // /cart — used by the floating cart button that appears once the header
  // is scrolled out of view.
  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    drawer.classList.add('active', 'is-open', 'animate');
    const overlay = document.querySelector('.cart-drawer-overlay') || document.getElementById('CartDrawer-Overlay');
    if (overlay) overlay.classList.add('active');
    document.body.classList.add('overflow-hidden');
  }

  // Applies a discount code via Shopify's native /discount/{code} endpoint
  // (sets the code against the checkout session, same mechanism as a direct
  // link) instead of a full page navigation, then refreshes the drawer so
  // totals reflect it. There's no dedicated Ajax endpoint for this, and the
  // redirect response gives no explicit valid/invalid signal, so success is
  // inferred by comparing cart.total_discount before and after the call.
  async function applyCoupon(form) {
    const input = form.querySelector('[data-coupon-input]');
    const btn = form.querySelector('[data-coupon-submit]');
    const code = (input && input.value ? input.value : '').trim();
    if (!code) return;

    if (btn) btn.disabled = true;
    if (input) input.disabled = true;

    try {
      const before = await getCart();
      await fetch(`/discount/${encodeURIComponent(code)}`, { headers: { Accept: 'text/html' } });
      await refreshDrawer();
      await syncTiers();
      const after = await getCart();
      const applied = after.total_discount > before.total_discount;

      const msg = document.querySelector('[data-coupon-msg]');
      const freshInput = document.querySelector('[data-coupon-input]');
      if (msg) {
        msg.hidden = false;
        msg.classList.toggle('is-success', applied);
        msg.classList.toggle('is-error', !applied);
        msg.textContent = applied ? `Code "${code}" appliqué !` : "Ce code n'est pas valide.";
      }
      if (applied && freshInput) freshInput.value = '';
    } catch (e) {
      if (btn) btn.disabled = false;
      if (input) input.disabled = false;
    }
  }

  /* ---------- Global listeners ---------- */

  // The header cart icon is a plain <a href="/cart"> for graceful
  // degradation (no-JS, right-click "open in new tab", etc.) — on a normal
  // left-click we intercept it and open the drawer instead, so visitors are
  // never sent to the /cart page. Wired up separately, in the capture phase,
  // with stopImmediatePropagation: this element is also a natural target for
  // the storefront's own remote cart bundle (loaded from a third party, see
  // notes elsewhere in this file) to attach its own handling to, and a plain
  // bubble-phase listener occasionally lost that race, letting the native
  // navigation through. Capturing first and stopping propagation outright
  // gives this the best chance of reliably winning every time.
  document.addEventListener('click', (event) => {
    const headerCartIcon = event.target.closest('#cart-icon-bubble');
    if (!headerCartIcon) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openCartDrawer();
  }, true);

  document.addEventListener('click', (event) => {
    const floatingCartBtn = event.target.closest('[data-floating-cart-btn]');
    if (floatingCartBtn) {
      event.preventDefault();
      openCartDrawer();
      return;
    }

    // Clicking the dimmed area to the left of the drawer closes it, without
    // relying on the theme's own overlay handler.
    const overlay = event.target.closest('#CartDrawer-Overlay');
    if (overlay) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
      return;
    }

    const chooseBtn = event.target.closest('[data-choose-gift-btn]');
    if (chooseBtn) {
      openChoiceScreen(chooseBtn.dataset.tierAmount);
      return;
    }

    if (event.target.closest('[data-gift-modal-close]') || event.target.closest('[data-gift-modal-backdrop]')) {
      closeGiftModal();
      return;
    }

    const viewCartLink = event.target.closest('[data-gift-view-cart]');
    if (viewCartLink) {
      event.preventDefault();
      closeGiftModal();
      openCartDrawer();
      return;
    }

    const option = event.target.closest('[data-gift-option]');
    if (option && !option.disabled) {
      modalSelection = option.dataset.variantId;
      document.querySelectorAll('[data-gift-option]').forEach((opt) => {
        opt.classList.toggle('is-selected', opt === option);
      });
      setConfirmState();
      return;
    }

    if (event.target.closest('[data-gift-confirm]')) {
      confirmGift();
      return;
    }

  });

  document.addEventListener('submit', (event) => {
    const couponForm = event.target.closest('[data-coupon-form]');
    if (couponForm) {
      event.preventDefault();
      applyCoupon(couponForm);
    }
  });

  document.addEventListener('keyup', (event) => {
    if (event.code === 'Escape') closeGiftModal();
  });

  // refreshDrawer() replaces the cart items' innerHTML wholesale, which itself
  // fires a large burst of mutation records back into this same observer —
  // debouncing collapses that self-triggered burst into a single syncTiers()
  // call instead of dozens of redundant /cart.js fetches in a row.
  let mutationDebounce = null;
  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some(
      (m) => !(m.target instanceof Element && m.target.closest('[data-rewards-bar], [data-gift-modal], [data-cart-gifts]'))
    );
    if (!relevant) return;
    clearTimeout(mutationDebounce);
    mutationDebounce = setTimeout(syncTiers, 200);
  });

  function observeCartItems() {
    const target = document.querySelector('#CartDrawer-CartItems');
    if (target) observer.observe(target, { childList: true, subtree: true, characterData: true });
  }

  // The theme's own add-to-cart flow (remote bundle) can replace the inner
  // drawer DOM wholesale, silently detaching the observer above from its
  // target. The <cart-drawer> element itself is never replaced though, so
  // watching its "active" class is a reliable, independent signal that the
  // drawer just opened (which happens right after every add-to-cart) and is
  // the moment we most need to re-check tiers / auto-open the gift modal.
  function observeDrawerOpen() {
    const root = document.querySelector('cart-drawer');
    if (!root) return;
    let wasActive = root.classList.contains('active');
    const drawerObserver = new MutationObserver(() => {
      const isActive = root.classList.contains('active');
      if (isActive && !wasActive) syncTiers();
      wasActive = isActive;
    });
    drawerObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- Reservation countdown (cosmetic urgency timer) ---------- */

  function startReservationTimer() {
    const STORAGE_KEY = 'mf_reservation_start';
    const DURATION = 15 * 60;
    let start = parseInt(sessionStorage.getItem(STORAGE_KEY), 10);
    if (!start || Number.isNaN(start)) {
      start = Date.now();
      sessionStorage.setItem(STORAGE_KEY, String(start));
    }

    function tick() {
      const elapsed = Math.floor((Date.now() - start) / 1000);
      let remaining = DURATION - (elapsed % DURATION);
      if (remaining <= 0) remaining = DURATION;
      const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
      const ss = String(remaining % 60).padStart(2, '0');
      document.querySelectorAll('[data-reservation-time]').forEach((el) => {
        el.textContent = `${mm}:${ss}`;
      });
    }

    tick();
    setInterval(tick, 1000);
  }

  let booted = false;
  function boot() {
    if (booted) return;
    booted = true;
    observeCartItems();
    observeDrawerOpen();
    startReservationTimer();
    // Give the theme's own page-load scripts a moment to settle before we
    // evaluate tiers, so a concurrent DOM refresh can't clobber the gift
    // modal's visibility right after we set it.
    setTimeout(syncTiers, 300);
    // Belt-and-suspenders: also re-check tiers every couple of seconds while
    // the drawer is open. Cheap (one /cart.js fetch) and guarantees the gift
    // modal auto-opens even if every DOM-mutation signal above were somehow
    // missed.
    setInterval(() => {
      const root = document.querySelector('cart-drawer');
      if (root && root.classList.contains('active')) syncTiers();
    }, 2000);
  }

  document.addEventListener('DOMContentLoaded', boot);
  if (document.readyState !== 'loading') boot();
})();
